import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { colors, typography } from '../constants';
import { Card, ChannelDot, NumberField, ScreenState } from '../components';
import {
  useAnalytics,
  useUpdateChannel,
  useUpdateProfitSettings,
} from '../hooks/useProfitData';
import { fmt, pct, pctVal } from '../lib/format';

const boxLabelSx = {
  fontSize: typography.caption,
  color: colors.faint,
  fontWeight: 600,
  textTransform: 'uppercase',
};

const boxValueSx = {
  fontSize: typography.emphasis,
  fontWeight: 800,
  color: colors.foreground,
};

const hintSx = { fontSize: typography.caption, color: colors.faint, mt: 0.25 };

const statBoxSx = {
  flex: 1,
  minWidth: 150,
  backgroundColor: colors.muted,
  borderRadius: 2,
  p: 1.25,
};

const outlineBoxSx = {
  flex: 1,
  minWidth: 150,
  backgroundColor: colors.card,
  borderRadius: 2.5,
  p: 1.25,
  border: `1px solid ${colors.border}`,
};

function Tip({ tone, children }) {
  const surface =
    tone === 'success'
      ? { bg: colors.successSurface, fg: colors.successSurfaceForeground }
      : { bg: colors.warningSurface, fg: colors.warningSurfaceForeground };
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 0.75,
        backgroundColor: surface.bg,
        borderRadius: 2,
        p: 1.25,
        mt: 0.25,
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 15, color: surface.fg, flexShrink: 0 }} />
      <Typography
        sx={{
          flex: 1,
          fontSize: typography.caption,
          color: surface.fg,
          lineHeight: 1.35,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function ChannelCard({ channel, data }) {
  const updateChannel = useUpdateChannel();
  const updateSettings = useUpdateProfitSettings();
  const [expanded, setExpanded] = useState(false);

  const net = channel.netRevenue;
  const profit = channel.profit;
  const profitColor = profit > 0 ? colors.success : colors.destructive;

  const commitCommission = (raw) => {
    const v = parseFloat(raw);
    if (isNaN(v) || v < 0) return;
    updateChannel.mutate({ id: channel.channelId, body: { commission: v } });
  };

  const commitCardFee = (raw) => {
    const v = parseFloat(raw);
    if (isNaN(v) || v < 0 || v > 10) return;
    updateSettings.mutate({ posCardFeePct: v });
  };

  return (
    <Card sx={{ p: 0, overflow: 'hidden' }}>
      <Box
        component="button"
        type="button"
        onClick={() => setExpanded((e) => !e)}
        sx={{
          width: '100%',
          px: 2,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.25,
          border: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          fontFamily: 'inherit',
          textAlign: 'left',
          '&:hover': { backgroundColor: colors.muted },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            flex: 1,
            minWidth: 0,
          }}
        >
          <ChannelDot color={channel.color} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: typography.body,
                color: colors.foreground,
              }}
            >
              {channel.name}
            </Typography>
            <Typography
              sx={{
                fontSize: typography.caption,
                color: colors.mutedForeground,
              }}
            >
              {fmt(channel.revenue)} · {pct(channel.sharePct)} of sales
            </Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography
            sx={{
              fontSize: typography.small,
              fontWeight: 800,
              color: profitColor,
            }}
          >
            {fmt(profit)}
          </Typography>
          <Typography
            sx={{ fontSize: typography.caption, color: colors.faint }}
          >
            profit
          </Typography>
          {expanded ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: colors.primary }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: colors.faint }} />
          )}
        </Box>
      </Box>

      {expanded ? (
        <Box
          sx={{
            borderTop: `1px solid ${colors.border}`,
            p: 2,
            backgroundColor: colors.muted,
          }}
        >
          {channel.isPOS ? (
            <>
              <Typography
                sx={{
                  fontSize: typography.caption,
                  fontWeight: 700,
                  color: colors.foreground,
                  mb: 1,
                }}
              >
                Payment mix
              </Typography>

              <Box
                sx={{ display: 'flex', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}
              >
                <Box
                  sx={{
                    ...outlineBoxSx,
                    border: `2px solid ${colors.primary}`,
                  }}
                >
                  <Typography
                    sx={{
                      ...boxLabelSx,
                      color: colors.primary,
                      mb: 0.75,
                    }}
                  >
                    Card %
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: typography.title,
                      fontWeight: 800,
                      color: colors.primary,
                    }}
                  >
                    {pct(data.posCardPct)}
                  </Typography>
                  <Typography sx={hintSx}>
                    {fmt(data.posCardRevenue)} on card
                  </Typography>
                </Box>

                <Box sx={outlineBoxSx}>
                  <Typography sx={{ ...boxLabelSx, mb: 0.75 }}>
                    Cash %
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: typography.title,
                      fontWeight: 800,
                      color: colors.success,
                    }}
                  >
                    {pct(100 - data.posCardPct)}
                  </Typography>
                  <Typography sx={hintSx}>
                    {fmt(data.posCashRevenue)} cash · no fee
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  backgroundColor: colors.card,
                  borderRadius: 2.5,
                  p: 1.25,
                  border: `2px solid ${colors.warning}`,
                  mb: 1.5,
                }}
              >
                <Typography
                  sx={{
                    ...boxLabelSx,
                    color: colors.warning,
                    mb: 0.75,
                  }}
                >
                  Card processing fee %
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <NumberField
                    initialValue={pctVal(data.cardFeePct)}
                    onCommit={commitCardFee}
                    width={56}
                    fontSize={20}
                    align="left"
                    color={colors.warning}
                    underlineColor={colors.warning}
                  />
                  <Typography
                    sx={{
                      fontSize: typography.emphasis,
                      fontWeight: 800,
                      color: colors.warning,
                    }}
                  >
                    %
                  </Typography>
                </Box>
                <Typography sx={hintSx}>
                  Effective fee on POS: {pct(channel.effCommission)} ·{' '}
                  {fmt((channel.revenue * channel.effCommission) / 100)} total
                </Typography>
              </Box>

              <Box
                sx={{ display: 'flex', gap: 1.25, mb: 1.25, flexWrap: 'wrap' }}
              >
                <Box sx={statBoxSx}>
                  <Typography sx={boxLabelSx}>After card fee</Typography>
                  <Typography sx={boxValueSx}>{fmt(net)}</Typography>
                </Box>
                <Box
                  sx={{
                    ...statBoxSx,
                    backgroundColor:
                      profit > 0
                        ? colors.successSurface
                        : colors.destructiveSurface,
                  }}
                >
                  <Typography sx={boxLabelSx}>Your profit</Typography>
                  <Typography sx={{ ...boxValueSx, color: profitColor }}>
                    {fmt(profit)}
                  </Typography>
                </Box>
              </Box>

              <Tip tone="success">
                The card and cash split is read from how your POS orders were
                paid. Cash payments have no processing fee, so the more cash, the
                lower your blended fee ({pct(data.posBlendedFee)}); the card fee
                only applies to the card portion.
              </Tip>
            </>
          ) : (
            <>
              <Box
                sx={{ display: 'flex', gap: 1.25, mb: 1.25, flexWrap: 'wrap' }}
              >
                <Box sx={outlineBoxSx}>
                  <Typography sx={boxLabelSx}>Revenue</Typography>
                  <Typography
                    sx={{
                      fontSize: typography.heading,
                      fontWeight: 800,
                      color: colors.foreground,
                    }}
                  >
                    {fmt(channel.revenue)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    ...outlineBoxSx,
                    border: `2px solid ${colors.primary}`,
                  }}
                >
                  <Typography sx={{ ...boxLabelSx, color: colors.primary }}>
                    Commission %
                  </Typography>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}
                  >
                    <NumberField
                      initialValue={pctVal(channel.commission)}
                      onCommit={commitCommission}
                      width={64}
                      fontSize={22}
                      align="left"
                    />
                    <Typography
                      sx={{
                        fontSize: typography.emphasis,
                        fontWeight: 800,
                        color: colors.primary,
                      }}
                    >
                      %
                    </Typography>
                  </Box>
                  <Typography sx={hintSx}>
                    {fmt((channel.revenue * channel.commission) / 100)} in fees
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{ display: 'flex', gap: 1.25, mb: 1.25, flexWrap: 'wrap' }}
              >
                <Box sx={statBoxSx}>
                  <Typography sx={boxLabelSx}>After fees</Typography>
                  <Typography sx={boxValueSx}>{fmt(net)}</Typography>
                </Box>
                <Box
                  sx={{
                    ...statBoxSx,
                    backgroundColor:
                      profit > 0
                        ? colors.successSurface
                        : colors.destructiveSurface,
                  }}
                >
                  <Typography sx={boxLabelSx}>Your profit</Typography>
                  <Typography sx={{ ...boxValueSx, color: profitColor }}>
                    {fmt(profit)}
                  </Typography>
                </Box>
              </Box>

              {channel.commission > 0 ? (
                <Tip tone="warning">
                  Negotiated a better rate? Update the % — Overview updates
                  instantly.
                </Tip>
              ) : null}
            </>
          )}
        </Box>
      ) : null}
    </Card>
  );
}

export default function Channels() {
  const query = useAnalytics();

  return (
    <ScreenState
      id="channels"
      query={query}
      loadingLabel="Loading channels…"
      emptyIcon={<GridViewOutlinedIcon fontSize="inherit" />}
      emptyTitle="No channels yet"
      emptyMessage="Import your sales data to see net revenue, profit and margin for each sales channel."
      allowNoData
    >
      {(data) => {
        const commissionFreePct =
          (data.channels
            .filter((c) => c.commission === 0)
            .reduce((s, c) => s + c.revenue, 0) /
            (data.totalRevenue || 1)) *
          100;

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography
                sx={{
                  fontSize: typography.caption,
                  color: colors.mutedForeground,
                  mb: 0.5,
                }}
              >
                Open any platform to edit its commission. Overview updates
                instantly.
              </Typography>
              <Typography
                sx={{ fontSize: typography.caption, color: colors.faint }}
              >
                Net revenue, profit &amp; margin per channel.
              </Typography>
            </Box>

            {data.channels.map((c) => (
              <ChannelCard key={c.channelId} channel={c} data={data} />
            ))}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                backgroundColor: colors.successSurface,
                borderRadius: 3,
                p: 1.75,
              }}
            >
              <CheckCircleOutlineIcon
                sx={{
                  fontSize: 17,
                  color: colors.successSurfaceForeground,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  flex: 1,
                  fontSize: typography.caption,
                  color: colors.successSurfaceForeground,
                  lineHeight: 1.6,
                }}
              >
                <b>{pct(commissionFreePct)}</b> of revenue is commission-free
                (POS + Direct). Strong position.
              </Typography>
            </Box>
          </Box>
        );
      }}
    </ScreenState>
  );
}
